import React, { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { isTokenExpired, getTimeUntilExpiry } from '../utils/jwtUtils';
import { initializeApiInterceptor } from '../utils/apiInterceptor';
import { buildApiUrl } from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('adminToken'));
    const [user, setUser] = useState(null);
    const expirationTimerRef = useRef(null);

    // Function to setup expiration timer
    const setupExpirationTimer = useCallback((token) => {
        // Clear existing timer
        if (expirationTimerRef.current) {
            clearTimeout(expirationTimerRef.current);
        }
        
        if (!token) return;
        
        // Check if token is already expired
        if (isTokenExpired(token)) {
            console.warn('Token is already expired');
            logout('expired');
            return;
        }
        
        // Get time until expiration
        const timeUntilExpiry = getTimeUntilExpiry(token);
        
        if (timeUntilExpiry > 0) {
            console.log(`Token expires in ${timeUntilExpiry} seconds`);
            
            // Set timer to logout 30 seconds before expiration or at expiration
            const logoutTime = Math.max(timeUntilExpiry - 30, 1) * 1000;
            
            expirationTimerRef.current = setTimeout(() => {
                logout('expired');
            }, logoutTime);
        }
    }, []);

    useEffect(() => {
        // Update localStorage when token changes
        if (token) {
            localStorage.setItem('adminToken', token);
            setupExpirationTimer(token);
        } else {
            localStorage.removeItem('adminToken');
        }
    }, [token, setupExpirationTimer]);

    const login = (newToken) => {
        setToken(newToken);
    };

    const logout = (reason = 'manual') => {
        console.log(`Logging out user (reason: ${reason})`);
        
        // Clear expiration timer
        if (expirationTimerRef.current) {
            clearTimeout(expirationTimerRef.current);
            expirationTimerRef.current = null;
        }
        
        // Clear token and user
        setToken(null);
        setUser(null);
        
        // Show notification if logout was due to token expiration
        if (reason === 'expired') {
            // You can add a toast notification here if needed
            console.warn('Session expired. Please login again.');
        }
    };

    const fetchUser = useCallback(async () => {
        if (!token) return;
        
        // Check token expiration before making request
        if (isTokenExpired(token)) {
            logout('expired');
            return;
        }
        
        try {
            const response = await fetch(buildApiUrl('admin/users/me'), {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            
            if (response.ok) {
                const data = await response.json();
                setUser(data);
            } else if (response.status === 401) {
                // Token is invalid or expired
                logout('expired');
            } else {
                setUser(null);
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
            setUser(null);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Initialize API interceptor with auth context
    const authContextValue = useMemo(() => ({
        token,
        login,
        logout,
        isAuthenticated: !!token && !isTokenExpired(token),
        user,
        fetchUser
    }), [token, user, fetchUser]);

    useEffect(() => {
        initializeApiInterceptor(authContextValue);
    }, [authContextValue]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (expirationTimerRef.current) {
                clearTimeout(expirationTimerRef.current);
            }
        };
    }, []);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};

